import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RevealState } from "./useReveal";

/**
 * A number that runs from `from` to `to` (up or down) while its illustration plays, easing out as it lands. Screen readers get only the
 * final number; the running one is hidden from them, so they never hear it change.
 *
 * @param props.from - Where the count starts.
 * @param props.to - Where it lands.
 * @param props.state - The illustration's reveal state: `from` while pending, counting while playing, `to` when still.
 * @param props.durationMs - How long the count takes.
 * @param props.delayMs - How long to wait before counting.
 * @param props.suffix - Text after the number, such as a per cent sign.
 * @param props.currency - An ISO currency code, to show the number as whole units of that currency.
 */
export function CountUp({
  from,
  to,
  state,
  durationMs = 1400,
  delayMs = 0,
  suffix = "",
  currency,
}: {
  from: number;
  to: number;
  state: RevealState;
  durationMs?: number;
  delayMs?: number;
  suffix?: string;
  currency?: string;
}): React.JSX.Element {
  const { i18n } = useTranslation();
  const [value, setValue] = useState(state === "still" ? to : from);

  useEffect(() => {
    if (state === "still") {
      return;
    }
    if (state === "pending") {
      setValue(from);
      return;
    }

    let frame = 0;
    let started = 0;
    const tick = (now: number): void => {
      started ||= now;
      const progress = Math.min(Math.max((now - started - delayMs) / durationMs, 0), 1);
      const eased = 1 - (1 - progress) ** 3;
      setValue(Math.round(from + (to - from) * eased));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [state, from, to, durationMs, delayMs]);

  const format = (amount: number): string =>
    new Intl.NumberFormat(i18n.language, currency ? { style: "currency", currency, maximumFractionDigits: 0 } : {}).format(amount) + suffix;

  return (
    <>
      <span aria-hidden="true">{format(state === "still" ? to : value)}</span>
      <span className="visually-hidden">{format(to)}</span>
    </>
  );
}
